import type { RiskEvidence } from "@/pages/safety-beta/components/RiskCard";
import { formatShortDate } from "@/utils/date-conversion";

export const formatHashtags = (hashtags: string[]): string => {
  if (hashtags.length === 0) return "No hashtags";
  return hashtags
    .map((tag) => {
      const cleanTag = tag.replace(/[[\]'"\s]/g, "").trim();
      return `#${cleanTag}`;
    })
    .join(", ");
};

export const formatCreatorId = (id: string): string => {
  return `@${id.replace("CID-", "")}`;
};

export const formatCreatorIdShort = (id: string): string => {
  return id.replace("CID-", "");
};

/**
 * Normalise a user-entered creator id into the stored `CID-<id>` form.
 * Accepts a raw id, an `@handle`, or an already-prefixed `CID-...` value.
 * Returns an empty string when there's nothing left after cleaning.
 */
export const normalizeCreatorId = (raw: string): string => {
  let cleaned = raw.trim();
  if (cleaned.startsWith("CID-")) cleaned = cleaned.slice(4);
  if (cleaned.startsWith("@")) cleaned = cleaned.slice(1);
  return cleaned ? `CID-${cleaned}` : "";
};

export const formatViewsCompact = (views: number | null | undefined): string => {
  if (views == null) return "—";
  if (views >= 1_000_000) return `${(views / 1_000_000).toFixed(1)}M`;
  if (views >= 1_000) return `${Math.round(views / 1_000)}k`;
  return String(views);
};

export const formatViews = (views: number | null | undefined): string => {
  if (views == null) return "—";
  if (views >= 1_000_000) return `${(views / 1_000_000).toFixed(1)}M`;
  if (views >= 10_000) return `${Math.round(views / 1_000)}k`;
  return views.toLocaleString();
};

// A risk a reviewer added manually (rather than one the model detected) carries an
// "add_flag" edit in its audit trail — derive the "Flagged by {name} • {date}" display
// from that, so the custom look survives a page reload instead of relying on session state.
export const resolveCustomRiskAttribution = (flag: {
  createdAt: string | Date;
  edits: { actionTaken: string; user: { firstName: string; lastName: string } | null }[];
}): { isCustom: boolean; flaggedByName?: string; flaggedAt?: string } => {
  const addEdit = flag.edits.find((edit) => edit.actionTaken === "add_flag");
  if (!addEdit) return { isCustom: false };

  return {
    isCustom: true,
    flaggedByName: addEdit.user ? `${addEdit.user.firstName} ${addEdit.user.lastName}`.trim() : undefined,
    flaggedAt: formatShortDate(flag.createdAt),
  };
};

export const formatEvidence = (native: string | null | undefined, english?: string | null): RiskEvidence[] => {
  if (!native) return [];

  const splitByTimestamps = (s: string): { ts: string; text: string }[] => {
    const parts = s.split(/\[(\d+:\d+)\]/);
    const result: { ts: string; text: string }[] = [];
    for (let i = 1; i < parts.length; i += 2) {
      const text = (parts[i + 1] ?? "").replace(/;/g, "").trim();
      if (text) result.push({ ts: parts[i], text });
    }
    return result;
  };

  const nativeParts = splitByTimestamps(native);

  if (nativeParts.length === 0) {
    const text = native.replace(/;/g, "").trim();
    return text ? [{ timestamp: "", text }] : [];
  }

  const englishParts = english ? splitByTimestamps(english) : [];

  return nativeParts.map((np, i) => {
    const en = englishParts[i]?.text ?? "";
    const item: RiskEvidence = { timestamp: np.ts, text: en || np.text };
    if (en && en !== np.text) item.translation = np.text;
    return item;
  });
};
